import {ChangedCompressedPath, CommentGenreEnum, PlayerColor} from '@types';
import {
    Ki,
    Sgf,
    initialRootNode,
    isMoveNode,
    isSetupNode,
    findProps,
    MarkupProp,
    getRoot,
    getMoveColor,
    findProp,
    extractBoardSize,
    CustomProp,
    DEFAULT_BOARD_SIZE,
    SGF_LETTERS,
    MoveProp,
    inPath,
    calcVariationsMarkup,
    Markup,
    TNode,
    isRightNode,
    sgfToA1,
    isTargetNode,
    inRightPath,
} from 'ghostban';
import {getCompressedPathFromNode} from './utils';
import {isEmpty} from 'lodash';
import {
    CUSTOM_COMMENT_REGEX,
    SPEED_SOLVE_FIRST_MOVE_TIME_LIMIT,
    SPEED_SOLVE_SUBSEQUENT_MOVE_TIME_LIMIT,
} from './const';

export const calcCommentsMarkup = () => {};

export const getSgfNodeIds = (sgf?: string): Set<string> | null => {
    if (!sgf) return null;

    const root = new Sgf(sgf).root;
    if (!root) return null;

    const nodeIds = new Set<string>();
    root.walk((node: TNode) => {
        nodeIds.add(node.model.id);
        return true;
    });

    return nodeIds;
};

export const getNodeA1Path = (node?: TNode | null, separator = ','): string => {
    if (!node) return '';

    const pathNodes = node.getPath();
    // Convert each node's move to A1 format
    return pathNodes
        .map((n: TNode) => {
            const moveProps = n.model?.moveProps?.[0];
            return moveProps ? sgfToA1(moveProps.toString()) : '';
        })
        .filter((move: string) => move)
        .join(separator);
};

export const findChildByA1Move = (
    node: TNode,
    move: string
): TNode | undefined => {
    return node.children?.find(child => {
        const moveProps = child.model?.moveProps?.[0];
        return moveProps ? sgfToA1(moveProps.toString()) === move : false;
    });
};

export const pathToUrlParam = (path: string): string => {
    return path.replace(/,/g, '-');
};

export const pathFromUrlParam = (urlPath: string): string => {
    return urlPath.replace(/-/g, ',');
};

export const getFullPath = (
    node?: TNode,
    activeBranchIndexes: {
        [key: string]: number;
    } = {}
): TNode[] => {
    if (!node) return [];
    const path = node.getPath();
    const index = activeBranchIndexes[node.model.id] || 0;
    let n = node.children[index];
    while (n) {
        path.push(n);
        const index = activeBranchIndexes[n.model.id] || 0;
        n = n.children[index];
    }
    return path;
};

export const getFirstToMoveColor = (sgf: string) => {
    const sgfParser = new Sgf(sgf);
    return getFirstToMoveColorFromRoot(sgfParser.root);
};

export const getPlayerColorFromSgf = (sgf: string): PlayerColor => {
    const sgfParser = new Sgf(sgf);
    const ki = getFirstToMoveColorFromRoot(sgfParser.root);
    if (ki === Ki.White) return 'white';
    if (ki === Ki.Black) return 'black';
    return null;
};

export const getFirstToMoveColorFromRoot = (root: TNode | null) => {
    if (!root) return Ki.Empty;

    const setupNode = getFirstSetupNodeFromNode(root);
    if (setupNode) {
        const firstMoveNode = setupNode.first(n => isMoveNode(n));
        if (firstMoveNode) {
            const firstMoveProp = firstMoveNode.model?.moveProps?.[0];
            if (firstMoveProp?.token && firstMoveProp.token === 'W') {
                return Ki.White;
            } else if (firstMoveProp?.token && firstMoveProp.token === 'B') {
                return Ki.Black;
            }
        }
    }
    return Ki.Empty;
};

const findFirstSetupNode = (root: TNode | null | undefined): TNode | null => {
    if (!root) return null;

    const {setupProps} = root.model;

    // If the root node does not include any setup properties
    // check whether its first child is a setup node (but not a move node)
    if (setupProps.length === 0) {
        const firstSetupNode = root.children.find(
            n => isSetupNode(n) && !isMoveNode(n)
        );
        return firstSetupNode || root;
    }

    return root;
};

export const getFirstSetupNodeFromSgf = (sgfString: string): TNode | null => {
    const sgf = new Sgf(sgfString);
    return findFirstSetupNode(sgf.root);
};

export const getFirstSetupNodeFromRoot = (
    root?: TNode | null
): TNode | null => {
    return findFirstSetupNode(root);
};

export const getFirstSetupNodeFromNode = (node: TNode | null) => {
    if (!node) return null;

    const root = getRoot(node);
    return getFirstSetupNodeFromRoot(root);
};

export const findNode = (
    currentNodeId: string,
    node: TNode
): TNode | undefined => {
    const root = node.getPath()[0];
    if (!root) return;
    return root.first(n => n.model.id === currentNodeId);
};

export const initRootNode = () => {
    const sgf = new Sgf(
        initialRootNode([
            'FF[4]',
            'GM[1]',
            'CA[UTF-8]',
            'AP[goproblems:0.1.0]',
            'SZ[19]',
            'ST[0]',
        ])
    );
    return sgf.root;
};

export const initSgf = () => {
    const sgf = new Sgf(
        initialRootNode([
            'FF[4]',
            'GM[1]',
            'CA[UTF-8]',
            'AP[goproblems:0.1.0]',
            'SZ[19]',
            'ST[0]',
        ])
    );
    return sgf.toSgf();
};

export const isEmptyNode = (node?: TNode) => {
    if (!node) return true;
    return (
        !node.hasChildren() &&
        node.model.id === '1b16b1' &&
        node.model.index === 0 &&
        node.model.number === 0 &&
        node.model.setupProps.length === 0 &&
        node.model.moveProps.length === 0 &&
        node.model.markupProps.length === 0 &&
        node.model.gameInfoProps.length === 0 &&
        node.model.nodeAnnotationProps.length === 0 &&
        node.model.moveAnnotationProps.length === 0 &&
        node.model.nodeAnnotationProps.length === 0 &&
        node.model.customProps.length === 0
    );
};

export const isSgfCommentNode = (node: TNode) => {
    const sgfCommentNode = findProp(node, 'C');
    return (
        sgfCommentNode &&
        !isEmpty(
            (sgfCommentNode?.value || '').replaceAll(CUSTOM_COMMENT_REGEX, '')
        )
    );
};

export const getExistChars = (node: TNode) => {
    if (!node) return;
    return findProps(node, 'LB')
        .map((markup: MarkupProp) => {
            return markup.values.map(value => value.split(':')[1]);
        })
        .flat()
        .join('');
};

export const findNextNumberMarkupText = (node: TNode) => {
    const existChars = getExistChars(node);
    const numbers = Array.from({length: 99}, (_, i) => i + 1);
    const nextNum =
        numbers.find(n => !existChars?.includes(n.toString()))?.toString() ||
        '0';
    return nextNum;
};

export const findNextCharMarkupText = (node: TNode) => {
    const existChars = getExistChars(node);
    const alphabet = Array.from({length: 26}, (_, i) =>
        String.fromCharCode(65 + i)
    );
    const nextChar = alphabet.find(l => !existChars?.includes(l)) || 'A';
    return nextChar;
};

export const findNodeFromCompressedPath = (
    root: TNode,
    compressedPath: string
) => {
    return root.first(
        (n: TNode) => getCompressedPathFromNode(n) === compressedPath
    );
};

export const hasNextPlayerMove = (node: TNode): boolean => {
    const root = getRoot(node);
    const playerColor = getFirstToMoveColorFromRoot(root);

    if (node.hasChildren()) {
        for (const n of node.children) {
            const nextPlayerMove = n.first(
                (n: TNode) => isMoveNode(n) && getMoveColor(n) === playerColor
            );
            if (nextPlayerMove) {
                return true;
            }
        }
    }
    return false;
};

export const getUndoMove = (currentNode: TNode, sgf: string) => {
    let node = currentNode?.parent;
    if (!node) return;
    const problemPlayerColor = getFirstToMoveColor(sgf);
    while (node && getMoveColor(node) === problemPlayerColor) {
        if (node.isRoot()) break;
        node = node?.parent;
    }
    return node;
};

const _rootCache = new Map<string, TNode | null>();
export const isOffPath = (node: TNode, sgf: string): boolean => {
    let root = _rootCache.get(sgf);
    if (root === undefined) {
        const problemTree = new Sgf(sgf);
        root = problemTree.root;
        _rootCache.set(sgf, root);
    }
    if (root) {
        return !root.first(n => n.model.id === node.model.id);
    }
    return false;
};

export const isExploringAfterSolution = (node: TNode, sgf: string): boolean => {
    if (!isOffPath(node, sgf)) {
        return false;
    }

    const path = node.getPath();
    return path.some(pathNode => pathNode !== node && isRightNode(pathNode));
};

export const isCommentTypeNode = (
    n: TNode,
    type = CommentGenreEnum.COMMENT
) => {
    const c = n.model.customProps?.find((p: CustomProp) => p.token === 'CT');
    return c?.value === type;
};

// Helper function to determine if a node is a computer move
export const isComputerNode = (node: TNode) => {
    const root = getRoot(node);
    return getFirstToMoveColorFromRoot(root) !== getMoveColor(node);
};

// Check if a node is reachable from root
// A node is reachable if all computer nodes on the path are in the first branch (getIndex === 0)
// OR if the path contains a TARGET node
export const isReachable = (node: TNode): boolean => {
    const path = node.getPath();

    // Check if path contains a TARGET node
    const hasTargetInPath = path.some(n => isTargetNode(n));
    if (hasTargetInPath) {
        return true;
    }

    // Check all computer nodes in the path
    for (const pathNode of path) {
        // If any computer node is not in the first branch, it's unreachable
        if (isComputerNode(pathNode) && pathNode.getIndex() !== 0) {
            return false;
        }
    }

    return true;
};

// Check if computer's choices will lead to wrong result
// eg: https://www.goproblems.com/problems/16057 D19
// Returns true only if computer has multiple choices and ALL lead to wrong path
// excludeNodeIds: optional set of node IDs to exclude (e.g. offPath nodes)
export const checkComputerWillLeadToWrong = (
    node: TNode,
    excludeNodeIds?: Set<string>
): boolean => {
    if (!node.parent || isComputerNode(node)) {
        return false;
    }

    const isIncluded = (n: TNode) =>
        !excludeNodeIds || !excludeNodeIds.has(n.model.id);

    // Check if node has any non-excluded children
    const includedChildren = node.children.filter(isIncluded);
    if (includedChildren.length === 0) return false;

    // Find all TARGET nodes in descendants (excluding offPath nodes)
    const allDescendants = node.all(() => true).filter(isIncluded);
    const allTargetNodes = allDescendants.filter(descendant =>
        isTargetNode(descendant)
    );

    const reachableTargetNodes = allTargetNodes.filter(targetNode =>
        isReachable(targetNode)
    );

    // If no TARGET nodes found, get the last computer node in the first branch
    if (reachableTargetNodes.length === 0) {
        // https://github.com/adum/goproblems2/issues/2603
        // A wrong first branch does not make the whole subtree wrong if any
        // sibling branch can still reach a RIGHT node.
        if (allDescendants.some(descendant => inRightPath(descendant))) {
            return false;
        }

        const firstBranchPath = [];
        let current = includedChildren[0];
        while (current) {
            firstBranchPath.push(current);
            const nextChildren = current.children.filter(isIncluded);
            current = nextChildren[0];
        }

        const lastComputerNode = firstBranchPath
            .reverse()
            .find(n => isComputerNode(n));

        if (lastComputerNode) {
            reachableTargetNodes.push(lastComputerNode);
        }
    }

    // Check if all reachable TARGET nodes have no RIGHT path in their descendants
    if (reachableTargetNodes.length > 0) {
        return reachableTargetNodes.every(targetNode => {
            const targetDescendants = targetNode
                .all(() => true)
                .filter(isIncluded);
            return !targetDescendants.some(descendant =>
                inRightPath(descendant)
            );
        });
    }

    return false;
};

// Check if player can lead to right path from this node
// Returns true if there exists any path to RIGHT in descendants
// AND all computer nodes in the path either have target or index === 0
export const checkPlayerCanLeadToRight = (node: TNode): boolean => {
    if (!inRightPath(node)) {
        return false;
    }

    // Find all RIGHT nodes in descendants
    const allDescendants = node.all(() => true);
    const rightNodes = allDescendants.filter(descendant =>
        isRightNode(descendant)
    );

    // For each RIGHT node, trace back to current node
    return rightNodes.some(rightNode => {
        const pathFromNodeToRight: TNode[] = [];
        let current: TNode | undefined = rightNode;

        // Walk back from right node to current node
        while (current && current.model.id !== node.model.id) {
            pathFromNodeToRight.unshift(current);
            current = current.parent;
        }

        if (!current || current.model.id !== node.model.id) {
            return false;
        }

        // Check all computer nodes in this path
        const allComputerNodesValid = pathFromNodeToRight
            .filter(pathNode => isComputerNode(pathNode))
            .every(
                computerNode =>
                    isTargetNode(computerNode) || computerNode.getIndex() === 0
            );

        return allComputerNodesValid;
    });
};

// Helper function to compute computerWillLeadToWrong with path checking
// Same logic as SgfTreePanel: checks if any node in path has computerWillLeadToWrong
export const getComputerWillLeadToWrong = (node: TNode): boolean => {
    const path = node.getPath();
    const hasComputerWillLeadToWrongInPath = path.some((pathNode: TNode) =>
        checkComputerWillLeadToWrong(pathNode)
    );
    return (
        hasComputerWillLeadToWrongInPath || checkComputerWillLeadToWrong(node)
    );
};

// export const isChangedNode = ()

export const calcCommentAndOffPathMarkup = (
    node: TNode,
    activeIndex = 0,
    sgf = '',
    defaultBoardSize = DEFAULT_BOARD_SIZE
) => {
    const markup = calcVariationsMarkup(node, 'append', activeIndex);
    const size = extractBoardSize(node, defaultBoardSize);

    enum CommentMarkup {
        CommentNode = Markup.DefaultDottedNode,
        CommentCorrectNode = Markup.PositiveDottedNode,
        CommentWrongNode = Markup.NegativeDottedNode,
        CommentQuestionNode = Markup.WarningDottedNode,
        CommentActiveNode = Markup.DefaultDottedActiveNode,
        CommentCorrectActiveNode = Markup.PositiveDottedActiveNode,
        CommentWrongActiveNode = Markup.NegativeDottedActiveNode,
        CommentQuestionActiveNode = Markup.WarningDottedActiveNode,
    }

    if (node.hasChildren()) {
        node.children.forEach((n: TNode) => {
            n.model.moveProps.forEach((m: MoveProp) => {
                const i = SGF_LETTERS.indexOf(m.value[0]);
                const j = SGF_LETTERS.indexOf(m.value[1]);
                if (i < 0 || j < 0) return;
                if (i < size && j < size) {
                    let mark;

                    // Check properties using the same logic as SgfTreePanel
                    const canLeadToRight = checkPlayerCanLeadToRight(n);
                    const computerWillLeadToWrong =
                        getComputerWillLeadToWrong(n);
                    const nodeIsReachable = isReachable(n);

                    if (
                        isOffPath(n, sgf) &&
                        inPath(n, n =>
                            isCommentTypeNode(n, CommentGenreEnum.COMMENT)
                        )
                    ) {
                        mark =
                            n.getIndex() === activeIndex
                                ? CommentMarkup.CommentActiveNode
                                : CommentMarkup.CommentNode;
                    } else if (
                        isOffPath(n, sgf) &&
                        inPath(n, n =>
                            isCommentTypeNode(n, CommentGenreEnum.CORRECT)
                        )
                    ) {
                        mark =
                            n.getIndex() === activeIndex
                                ? CommentMarkup.CommentCorrectActiveNode
                                : CommentMarkup.CommentCorrectNode;
                    } else if (
                        isOffPath(n, sgf) &&
                        inPath(n, n =>
                            isCommentTypeNode(n, CommentGenreEnum.WRONG)
                        )
                    ) {
                        mark =
                            n.getIndex() === activeIndex
                                ? CommentMarkup.CommentWrongActiveNode
                                : CommentMarkup.CommentWrongNode;
                    } else if (
                        isOffPath(n, sgf) &&
                        inPath(n, n =>
                            isCommentTypeNode(n, CommentGenreEnum.QUESTION)
                        )
                    ) {
                        mark =
                            n.getIndex() === activeIndex
                                ? CommentMarkup.CommentQuestionActiveNode
                                : CommentMarkup.CommentQuestionNode;
                    } else if (isOffPath(n, sgf)) {
                        mark =
                            n.getIndex() === activeIndex
                                ? Markup.DefaultDashedActiveNode
                                : Markup.DefaultDashedNode;
                    } else {
                        if (canLeadToRight) {
                            mark =
                                n.getIndex() === activeIndex
                                    ? Markup.PositiveActiveNode
                                    : Markup.PositiveNode;
                        } else {
                            mark =
                                n.getIndex() === activeIndex
                                    ? Markup.NegativeActiveNode
                                    : Markup.NegativeNode;
                        }

                        // Override: if computer will lead to wrong and node is not reachable, use red
                        if (computerWillLeadToWrong && !nodeIsReachable) {
                            mark =
                                n.getIndex() === activeIndex
                                    ? Markup.NegativeActiveNode
                                    : Markup.NegativeNode;
                        }
                    }

                    if (mark) {
                        const regex = new RegExp(
                            `\\b(${[
                                Markup.PositiveNode,
                                Markup.PositiveActiveNode,
                                Markup.NegativeNode,
                                Markup.NegativeActiveNode,
                                Markup.NeutralNode,
                                Markup.NeutralActiveNode,
                                Markup.WarningNode,
                                Markup.WarningActiveNode,
                                Markup.DefaultNode,
                                Markup.DefaultActiveNode,
                            ].join('|')})\\b\\|?`,
                            'g'
                        );

                        markup[i][j] =
                            markup[i][j].replace(regex, '') + '|' + mark;
                    }
                }
            });
        });
    }

    return markup;
};

export const inUserCompressedPath = (
    node: TNode,
    compressedPaths: string[]
) => {
    const nodeCompressedPath = getCompressedPathFromNode(node);
    return (
        compressedPaths.filter(path => {
            return (
                path.startsWith(nodeCompressedPath) &&
                !isEmpty(nodeCompressedPath)
            );
        }).length > 0
    );
};

export const isUserCompressedPathNode = (
    node: TNode,
    compressedPaths: string[]
) => {
    const nodeCompressedPath = getCompressedPathFromNode(node);
    return (
        compressedPaths.filter(path => {
            return path === nodeCompressedPath;
        }).length > 0
    );
};

export const isLatestUserCommentNode = (
    node: TNode,
    commentCompressedPaths: string[]
) => {
    const nodeCompressedPath = getCompressedPathFromNode(node);
    const latestComment =
        commentCompressedPaths[commentCompressedPaths.length - 1];
    return latestComment === nodeCompressedPath;
};

export const inChangedCompressedPaths = (
    node: TNode,
    changedCompressedPaths: ChangedCompressedPath[]
) => {
    const nodeCompressedPath = getCompressedPathFromNode(node);
    console.log('--ncp', nodeCompressedPath);
    console.log('--changedCompressedPaths', changedCompressedPaths);
    const result =
        changedCompressedPaths.filter(p => {
            return (
                p.path.startsWith(nodeCompressedPath) &&
                !isEmpty(nodeCompressedPath)
            );
        }).length > 0;
    console.log(
        '--results',
        changedCompressedPaths.filter(p => {
            return (
                p.path.startsWith(nodeCompressedPath) &&
                !isEmpty(nodeCompressedPath)
            );
        })
    );
    return result;
};

export const isSpeedSolve = (moveTimes: number[]): boolean => {
    if (moveTimes.length === 0) return false;

    if (moveTimes[0] > SPEED_SOLVE_FIRST_MOVE_TIME_LIMIT) {
        return false;
    }

    for (let i = 1; i < moveTimes.length; i++) {
        if (moveTimes[i] > SPEED_SOLVE_SUBSEQUENT_MOVE_TIME_LIMIT) {
            return false;
        }
    }

    return true;
};

export const isSpeedPlay = (moveTimes: number[]): boolean => {
    if (moveTimes.length === 0) return false;

    for (let i = 1; i < moveTimes.length; i++) {
        if (moveTimes[i] > SPEED_SOLVE_SUBSEQUENT_MOVE_TIME_LIMIT) {
            return false;
        }
    }

    return true;
};
